/**
 * The room behind the vault door: a black room, a big solid-gold Alpha mark floating high in a
 * shaft of warm light, turning slowly; the early-access form sits under it (the page places it
 * from markBottom). Fetched in the background once the closed vault is up (the vault calls
 * loadTreasure), shown when the door starts to swing.
 *
 * Asset contract (public/3d/alpha-ops/interior/, from the Blender pipeline: ext_preview.py
 * --behind alphamark --view open --export-interior, then scripts/web/build_web.py):
 *   interior.glb   room shell (TEXCOORD_0 = lightmap atlas) and the gold mark, facing the door;
 *                  placeholder materials matched by name
 *   lightmap.webp  baked Cycles diffuse light, encoded (E / lightmapScale) ^ (1/3): the room is
 *                  mostly dark, the cube root keeps its detail in 8 bits (decoded in the shader)
 *   env.exr        equirect seen from the gold, the spot lights as small hot discs: the bars'
 *                  reflections and highlights
 *
 * The beam and the dust in it are drawn here (additive, no textures).
 */
import * as THREE from 'three';

const BASE = '/3d/alpha-ops/interior/';

const SPIN = 0.62; // rad/s: one slow turn every ~10 s
const GOLD_ENV = 1.5; // the probe's cards read dimmer through the page's tone mapping than in Cycles
const REST_SPIN = THREE.MathUtils.degToRad(28); // the still pose (reduced motion): as in the concept

/** Blender (Z-up, metres) -> three.js (Y-up). */
const b2t = (x, y, z) => new THREE.Vector3(x, z, -y);

/** The lightmap is stored as a cube root: cube it back before it lights anything. */
const cubeRootLightmap = (shader) => {
  shader.fragmentShader = shader.fragmentShader.replace(
    '#include <lights_fragment_maps>',
    THREE.ShaderChunk.lights_fragment_maps.replace(
      'lightMapTexel.rgb * lightMapIntensity',
      'pow( lightMapTexel.rgb, vec3( 3.0 ) ) * lightMapIntensity',
    ),
  );
};

function createBeam(from, to, angleDeg, color) {
  const height = from.distanceTo(to);
  const bottom = height * Math.tan(THREE.MathUtils.degToRad(angleDeg / 2));
  const geo = new THREE.CylinderGeometry(0.06, bottom, height, 48, 1, true);
  const mat = new THREE.ShaderMaterial({
    uniforms: { level: { value: 0 }, time: { value: 0 }, color: { value: color } },
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
    vertexShader: `
      varying vec2 vUv;
      varying vec3 vNormalV;
      varying vec3 vPosV;
      void main() {
        vUv = uv;
        vec4 mv = modelViewMatrix * vec4( position, 1.0 );
        vPosV = mv.xyz;
        vNormalV = normalize( normalMatrix * normal );
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: `
      uniform float level;
      uniform float time;
      uniform vec3 color;
      varying vec2 vUv;
      varying vec3 vNormalV;
      varying vec3 vPosV;
      void main() {
        float facing = abs( dot( normalize( vNormalV ), normalize( -vPosV ) ) );
        float body = pow( facing, 2.2 );                       // soft edges, a denser core
        float along = pow( vUv.y, 2.2 ) * smoothstep( 0.0, 0.3, vUv.y ) * smoothstep( 1.0, 0.9, vUv.y ); // thins out downward
        float drift = 0.88 + 0.12 * sin( vUv.x * 40.0 + vUv.y * 7.0 + time * 0.4 );
        gl_FragColor = vec4( color * body * along * drift * level * 0.07, 1.0 );
      }`,
  });
  const beam = new THREE.Mesh(geo, mat);
  beam.position.copy(from).add(to).multiplyScalar(0.5);
  beam.renderOrder = 6;
  return beam;
}

/** Dust motes drifting in and around the beam, catching its light. */
function createDust(axis, yMin, yMax, radius, count) {
  const pos = new Float32Array(count * 3);
  const seed = new Float32Array(count);
  for (let i = 0; i < count; i++) {
    const r = radius * Math.sqrt(Math.random()) * 1.4;
    const a = Math.random() * Math.PI * 2;
    pos.set([axis.x + Math.cos(a) * r, yMin + Math.random() * (yMax - yMin), axis.z + Math.sin(a) * r], i * 3);
    seed[i] = Math.random();
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1));
  const mat = new THREE.ShaderMaterial({
    uniforms: {
      level: { value: 0 },
      time: { value: 0 },
      axis: { value: new THREE.Vector2(axis.x, axis.z) },
      top: { value: yMax },
      spread: { value: radius / Math.max(0.001, yMax - yMin) },
      pixelRatio: { value: 1 },
    },
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    vertexShader: `
      attribute float aSeed;
      uniform float time;
      uniform vec2 axis;
      uniform float top;
      uniform float spread;
      uniform float pixelRatio;
      varying float vLight;
      varying float vTwinkle;
      void main() {
        vec3 p = position;
        float t = time * ( 0.04 + aSeed * 0.05 );
        p += vec3( sin( t * 3.0 + aSeed * 40.0 ), sin( t * 1.6 + aSeed * 17.0 ) * 0.5, cos( t * 2.4 + aSeed * 29.0 ) ) * 0.12;
        // inside the cone (which widens downward) the motes are lit
        float coneR = max( 0.06, ( top - p.y ) * spread );
        vLight = smoothstep( coneR, coneR * 0.35, length( p.xz - axis ) );
        vTwinkle = 0.6 + 0.4 * sin( time * ( 1.3 + aSeed * 2.5 ) + aSeed * 50.0 );
        vec4 mv = modelViewMatrix * vec4( p, 1.0 );
        gl_PointSize = min( ( 1.2 + aSeed * 2.4 ) * pixelRatio * ( 4.0 / -mv.z ), 5.0 * pixelRatio );
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: `
      uniform float level;
      varying float vLight;
      varying float vTwinkle;
      void main() {
        float d = length( gl_PointCoord - 0.5 );
        float a = smoothstep( 0.5, 0.0, d ) * mix( 0.04, 1.0, vLight ) * vTwinkle * level;
        gl_FragColor = vec4( vec3( 1.0, 0.82, 0.58 ) * a * 0.8, 1.0 );
      }`,
  });
  const points = new THREE.Points(geo, mat);
  points.frustumCulled = false;
  points.renderOrder = 7;
  return points;
}

export async function loadTreasure(renderer, gltf, texLoader, exrLoader, opts = {}) {
  const M = await (await fetch(BASE + 'manifest.json')).json();
  const loadTex = (url) => new Promise((resolve, reject) => texLoader.load(BASE + url, resolve, undefined, reject));
  const [room, lightmap, env] = await Promise.all([
    gltf.loadAsync(BASE + 'interior.glb'),
    loadTex(M.textures.lightmap),
    exrLoader.loadAsync(BASE + M.textures.env),
  ]);
  const disposables = [lightmap];
  lightmap.colorSpace = THREE.NoColorSpace; // cube-root encoded data, not a colour
  lightmap.flipY = false;
  lightmap.channel = 0;
  env.mapping = THREE.EquirectangularReflectionMapping;
  const pmrem = new THREE.PMREMGenerator(renderer);
  const envRT = pmrem.fromEquirectangular(env);
  pmrem.dispose();
  env.dispose();
  disposables.push(envRT);

  const lmFull = M.lightmapScale * Math.PI;
  const baked = [];
  const lambert = (color) => {
    const m = new THREE.MeshLambertMaterial({ color, lightMap: lightmap, lightMapIntensity: 0 });
    m.onBeforeCompile = cubeRootLightmap;
    baked.push(m);
    disposables.push(m);
    return m;
  };
  const gold = new THREE.MeshPhysicalMaterial({
    color: new THREE.Color(1.0, 0.71, 0.29),
    metalness: 1,
    roughness: 0.18,
    envMap: envRT.texture,
    envMapIntensity: 0,
  });
  gold.envMapRotation.set(0, Math.PI / 2, 0); // the probe faced the back wall, like the door's
  disposables.push(gold);
  const mats = {
    Ops_BehindBlack: lambert(new THREE.Color(0.004, 0.004, 0.004)),
    Ops_BehindFloor: lambert(new THREE.Color(0.006, 0.006, 0.006)),
    Ops_Plinth: lambert(new THREE.Color(0.012, 0.012, 0.013)),
    Ops_DepositSteel: lambert(new THREE.Color(0.045, 0.036, 0.026)), // dark bronze: the wall stays in shadow
    Ops_DepositFrame: lambert(new THREE.Color(0.012, 0.011, 0.01)),
    Ops_Brass: lambert(new THREE.Color(0.2, 0.14, 0.06)),
    Ops_Keyhole: lambert(new THREE.Color(0.004, 0.004, 0.004)),
    Ops_HeroGold: gold,
  };
  room.scene.traverse((o) => {
    const mesh = o;
    if (!mesh.isMesh) return;
    const name = mesh.material.name;
    const m = mats[name];
    if (m) mesh.material = m;
    else console.warn('[alpha-ops] treasure room: no material for', name, mesh.name);
  });
  disposables.push({ dispose: () => room.scene.traverse((o) => o.geometry?.dispose()) });

  const group = new THREE.Group();
  group.add(room.scene);
  room.scene.updateMatrixWorld(true);
  const mark = M.mark ? (room.scene.getObjectByName(M.mark.name) ?? null) : null;
  let markBottom = null;
  const markRest = { y: 0, rot: 0 };
  if (mark) {
    // it turns about its own vertical axis, so its lowest point never moves
    const box = new THREE.Box3().setFromObject(mark);
    markBottom = new THREE.Vector3(mark.position.x, box.min.y, mark.position.z);
    markRest.y = mark.position.y;
    markRest.rot = mark.rotation.y;
    mark.rotation.y = markRest.rot + REST_SPIN;
  } else if (M.mark) {
    console.warn('[alpha-ops] treasure room: no object named', M.mark.name);
  }
  const [sx, sy, sz] = M.shaft.from;
  const from = b2t(sx, sy, sz);
  const to = b2t(sx, sy, M.shaft.to ?? M.plinthTop ?? 0);
  const beam = createBeam(from, to, M.shaft.angleDeg, new THREE.Color(...M.shaft.color));
  const dust = createDust(
    to,
    to.y + 0.05,
    from.y - 0.3,
    from.distanceTo(to) * Math.tan(THREE.MathUtils.degToRad(M.shaft.angleDeg / 2)),
    220,
  );
  group.add(beam, dust);
  disposables.push(beam.geometry, beam.material, dust.geometry, dust.material);
  const beamU = beam.material.uniforms;
  const dustU = dust.material.uniforms;

  return {
    group,
    markBottom,
    setLevel(level) {
      for (const m of baked) m.lightMapIntensity = lmFull * level;
      gold.envMapIntensity = level * GOLD_ENV;
      beamU.level.value = level;
      dustU.level.value = level;
      dustU.pixelRatio.value = renderer.getPixelRatio();
    },
    update(time) {
      beamU.time.value = time;
      dustU.time.value = time;
      if (mark && !opts.reducedMotion) {
        mark.rotation.y = markRest.rot + REST_SPIN + time * SPIN;
        mark.position.y = markRest.y + Math.sin(time * 0.9) * 0.012; // floating: the slightest bob
      }
    },
    dispose() {
      disposables.forEach((d) => d.dispose());
    },
  };
}
